import React, {useState, useMemo, useEffect} from "react";
import 'simplebar-react/dist/simplebar.min.css';
import ModalSuccess from "../../../../../components/shared/modals/modalSuccess";
import ModalDelete from "../../../../../components/shared/modals/modalDelete";
import DynamicTable from "../../../../../components/tables/oc/provider/dynamicTable";
import InputField from "../../../../../components/forms/inputField";
import SelectField from "../../../../../components/forms/selectOption";
import ModalAlertWarning from "../../../../../components/shared/modals/modalAlertWarning";
import {createBuyersGroup, getBuyersGroupList, updateBuyersGroup, softDeleteBuyersGroup, getAreasList } from "../../../../../client/areas/index"
import FilterInput from "../../../../../components/filters/filterInput/index"

export default function BuyersGroup() {
  const [isLoading, setIsLoading] = useState(true);
  const [purchasingGroups, setPurchasingGroups] = useState([]);
  const [areas, setAreas] = useState([]);
  const [formData, setFormData] = useState({
    group: '',
    areaId: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [isFormChanged, setIsFormChanged] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);

  const [searchTerm, setSearchTerm] = useState('');

  const [showModalSuccess, setShowModalSuccess] = useState(false);
  const [modalSuccessText, setModalSuccessText] = useState('');

  const [showModalDelete, setShowModalDelete] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const [showModalAlertWarning, setShowModalAlertWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;

  const filteredPurchasingGroups = useMemo(() => {
    if (!searchTerm) return purchasingGroups;
    
    return purchasingGroups.filter(group => {
      const matchesGroup = group.group.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesArea = group.area?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesGroup || matchesArea;
    });
  }, [purchasingGroups, searchTerm]);

  const currentRecords = filteredPurchasingGroups.slice(indexOfFirstRecord, indexOfLastRecord);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [groupsResponse, areasResponse] = await Promise.all([
        getBuyersGroupList(),
        getAreasList()
      ]);

      setPurchasingGroups(groupsResponse.data.purchasingGroups || []);
      setAreas(areasResponse.data || []);
    } catch (error) {
      console.error("Error al cargar datos:", error);
      setErrorMessage('Error al cargar los datos');
      setShowModalAlertWarning(true);
    } finally {
      setIsLoading(false);
    }
  };

  const changePage = (pageNumber) => {
    setCurrentPage(pageNumber);
    scrollToTopPagination();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setIsFormChanged(true);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.group || !formData.areaId) {
        setErrorMessage('Todos los campos son obligatorios');
        setShowModalAlertWarning(true);
        return;
      }

      if (editingId) {
        await updateBuyersGroup(editingId, formData);
        setModalSuccessText('Grupo de compra actualizado correctamente');
      } else {
        await createBuyersGroup(formData);
        setModalSuccessText('Grupo de compra creado correctamente');
      }

      setShowModalSuccess(true);
      resetForm();
      fetchData();
      scrollToTop();
    } catch (error) {
      if (error.response) {
        if (error.response.status === 409) {
          setErrorMessage(error.response.data.message || 'El nombre del grupo de compra ya existe');
        } 
        else if (error.response.status === 404) {
          setErrorMessage(error.response.data.message || 'Recurso no encontrado');
        }
        else {
          setErrorMessage(error.response.data.message || 'Error al procesar la solicitud');
        }
      } 
      else {
        setErrorMessage('Error de conexión. Por favor intente nuevamente');
      }
      setShowModalAlertWarning(true);
    }
  };

  const handleEdit = (group) => {
    setFormData({
      group: group.group,
      areaId: group.area?.id || group.areaId
    });
    setEditingId(group.id);
    setIsFormChanged(false);
    scrollToTop();
  };

  const handleDelete = (id) => {
    setSelectedGroupId(id);
    setShowModalDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await softDeleteBuyersGroup(selectedGroupId);
      setModalSuccessText('Grupo de compra eliminado correctamente');
      setShowModalSuccess(true);
      fetchData();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Error al eliminar el grupo de compra');
      setShowModalAlertWarning(true);
    } finally {
      setShowModalDelete(false);
    }
  };

  const resetForm = () => {
    setFormData({
      group: '',
      areaId: ''
    });
    setEditingId(null);
    setIsFormChanged(false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 85, behavior: "smooth" });
  };

  const scrollToTopPagination = () => {
    window.scrollTo({ top: 290, behavior: "smooth" });
  };

  const closeModalSuccess = () => {
  setShowModalSuccess(false);
  };

  const isFormEmpty = !formData.group && !formData.areaId;

  const tableConfig = {
    headers: ['#', 'Grupo de compra', 'Área', 'Fecha de registro', 'Acciones'],
    accessors: [
        'id',
        'group',
        'areaName',
        'createdAt',
        'acciones',

    ],
    widths: ['65px', '150px', '150px', '120px', '100px']
  };

  return (
    <div>
      <div className="flex flex-col items-start justify-center gap-4 ">
        <div className="flex items-center justify-center w-full">
          <div className="bg-[#00769633] flex md:flex-row flex-col  items-center justify-center gap-6 w-full rounded-2xl p-4">
            <div className="flex flex-row md:flex-col w-full md:w-[30%] justify-center gap-4 items-center md:items-start">
              <p className="text-2xl text-left text-blue font-itcdemi">
                {editingId ? 'Editar grupo de compra' : 'Agregar nuevo grupo de compra'}
              </p>
              <svg width="73" height="73" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.9375 6.125C15.5231 6.125 15.1257 6.28962 14.8326 6.58265C14.5396 6.87567 14.375 7.2731 14.375 7.6875V23.0562C13.4435 22.4075 12.3735 21.9852 11.25 21.8229V7.6875C11.25 6.4443 11.7439 5.25201 12.6229 4.37294C13.502 3.49386 14.6943 3 15.9375 3H25.0646C25.6802 2.99999 26.2897 3.12124 26.8584 3.3568C27.4271 3.59237 27.9439 3.93764 28.3792 4.37292L38.0021 13.9958C38.8812 14.875 39.375 16.0667 39.375 17.3083V34.7708C39.375 36.014 38.8811 37.2063 38.0021 38.0854C37.123 38.9645 35.9307 39.4583 34.6875 39.4583H29.6979C29.3445 38.2737 28.6957 37.1983 27.8125 36.3333H34.6875C35.1019 36.3333 35.4993 36.1687 35.7924 35.8757C36.0854 35.5827 36.25 35.1852 36.25 34.7708V17.5792H29.4875C28.2443 17.5792 27.052 17.0853 26.1729 16.2062C25.2939 15.3272 24.8 14.1349 24.8 12.8917V6.125H15.9375ZM27.925 8.3375V12.8917C27.925 13.7542 28.625 14.4542 29.4875 14.4542H34.0396L27.925 8.3375ZM34.6875 44.6667H29.2958C29.7458 43.7188 29.9979 42.6604 29.9979 41.5417H34.6854C36.4812 41.5417 38.2033 40.8283 39.4731 39.5585C40.7429 38.2888 41.4562 36.5666 41.4562 34.7708V17.4521L43.2104 19.2021C44.0896 20.0833 44.5833 21.275 44.5833 22.5187V34.7708C44.5833 37.3954 43.5407 39.9124 41.6849 41.7682C39.8291 43.6241 37.312 44.6667 34.6875 44.6667ZM10.2083 34.25C11.5897 34.25 12.9144 33.7013 13.8912 32.7245C14.8679 31.7478 15.4167 30.423 15.4167 29.0417C15.4167 27.6603 14.8679 26.3356 13.8912 25.3588C12.9144 24.3821 11.5897 23.8333 10.2083 23.8333C8.827 23.8333 7.50224 24.3821 6.52549 25.3588C5.54873 26.3356 5 27.6603 5 29.0417C5 30.423 5.54873 31.7478 6.52549 32.7245C7.50224 33.7013 8.827 34.25 10.2083 34.25ZM10.2083 26.9583C10.7609 26.9583 11.2908 27.1778 11.6815 27.5685C12.0722 27.9592 12.2917 28.4891 12.2917 29.0417C12.2917 29.5942 12.0722 30.1241 11.6815 30.5148C11.2908 30.9055 10.7609 31.125 10.2083 31.125C9.6558 31.125 9.12589 30.9055 8.73519 30.5148C8.34449 30.1241 8.125 29.5942 8.125 29.0417C8.125 28.4891 8.34449 27.9592 8.73519 27.5685C9.12589 27.1778 9.6558 26.9583 10.2083 26.9583ZM22.7083 46.75C24.0897 46.75 25.4144 46.2013 26.3912 45.2245C27.3679 44.2478 27.9167 42.923 27.9167 41.5417C27.9167 40.1603 27.3679 38.8356 26.3912 37.8588C25.4144 36.8821 24.0897 36.3333 22.7083 36.3333C21.327 36.3333 20.0022 36.8821 19.0255 37.8588C18.0487 38.8356 17.5 40.1603 17.5 41.5417C17.5 42.923 18.0487 44.2478 19.0255 45.2245C20.0022 46.2013 21.327 46.75 22.7083 46.75ZM22.7083 39.4583C23.2609 39.4583 23.7908 39.6778 24.1815 40.0685C24.5722 40.4592 24.7917 40.9891 24.7917 41.5417C24.7917 42.0942 24.5722 42.6241 24.1815 43.0148C23.7908 43.4055 23.2609 43.625 22.7083 43.625C22.1558 43.625 21.6259 43.4055 21.2352 43.0148C20.8445 42.6241 20.625 42.0942 20.625 41.5417C20.625 40.9891 20.8445 40.4592 21.2352 40.0685C21.6259 39.6778 22.1558 39.4583 22.7083 39.4583ZM25.375 28.5833C25.5285 28.4403 25.6516 28.2678 25.737 28.0761C25.8224 27.8845 25.8684 27.6776 25.8721 27.4678C25.8758 27.258 25.8372 27.0496 25.7586 26.855C25.68 26.6604 25.563 26.4837 25.4147 26.3353C25.2663 26.187 25.0896 26.07 24.895 25.9914C24.7004 25.9128 24.492 25.8742 24.2822 25.8779C24.0724 25.8816 23.8655 25.9276 23.6739 26.013C23.4822 26.0984 23.3097 26.2215 23.1667 26.375L7.54167 42C7.38815 42.143 7.26502 42.3155 7.17962 42.5072C7.09422 42.6989 7.0483 42.9058 7.0446 43.1156C7.0409 43.3254 7.07949 43.5338 7.15808 43.7283C7.23666 43.9229 7.35363 44.0996 7.502 44.248C7.65038 44.3964 7.82711 44.5133 8.02167 44.5919C8.21623 44.6705 8.42462 44.7091 8.63442 44.7054C8.84422 44.7017 9.05112 44.6558 9.24279 44.5704C9.43445 44.485 9.60695 44.3618 9.75 44.2083L25.375 28.5833Z" fill="#007696"/>
              </svg>
            </div>                  
            <div className="flex flex-col items-start justify-between w-full md:w-[40%] gap-3">
              <div className="flex flex-col w-full gap-2">
                <p className="font-itcdemi text-[#1F1F1F] lg:text-[14px]  text-[16px]">Nombre del grupo de compra: <span className="text-red-500">*</span></p>
                <InputField
                  onChange={handleInputChange}
                  placeholder="Describa"
                  value={formData.group}
                  name="group"
                  maxLength={5}
                  validation="textNumeric"
                />
              </div>
              <div className="flex flex-col w-full gap-2">
                <p className="font-itcdemi text-[#1F1F1F]  lg:text-[14px]  text-[16px]">Escoja el área: <span className="text-red-500">*</span></p>
                <SelectField 
                  placeholder="Escoger"
                  name="areaId"
                  value={formData.areaId || ''}
                  onChange={(e) => {
                    handleInputChange(e);
                    setIsFormChanged(true);
                  }}
                  options={areas.map(area => ({
                    value: area.id,
                    label: area.name
                  }))}
                />
              </div>
            </div>       
            <div className="flex flex-row md:flex-col justify-center items-center h-full w-full md:w-[30%] gap-6">
              <button
                onClick={handleSubmit}
                disabled={
                  (formData.group.length === 0 || formData.group.length > 5) || 
                    !formData.areaId || 
                  (!isFormChanged && editingId) 
                }
                className={`text-white bg-blue rounded-lg w-[161px] h-[50px] font-itcdemi text-[18px] 
                  ${(formData.group.length === 0 || formData.group.length > 5 || !formData.areaId || (!isFormChanged && editingId))
                    ? 'bg-gray cursor-not-allowed' 
                    : 'cursor-pointer'
                  }`}
              >
                Guardar
              </button>  
              <button
                onClick={resetForm}
                disabled={isFormEmpty}
                className={`text-blue w-[161px] h-[50px] font-itcdemi rounded-lg text-[18px] border-[2px] border-blue
                  ${isFormEmpty ? 'border-gray text-gray cursor-not-allowed' : 'cursor-pointer'}`}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
        <FilterInput 
          title="Buscar"
          value={searchTerm}
          onChange={(e) => {
            const sanitizedValue = e.target.value.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]/g, '');
            setSearchTerm(sanitizedValue);
            setCurrentPage(1);
          }}
          placeholder="Por grupo o área"
          classname="w-[200px]"
        />        
        <div className="w-full">
          {isLoading ? (
            <div>Cargando datos...</div>
          ) : (
            <div className="w-full mt-4 overflow-hidden overflow-x-auto">
              <DynamicTable
                data={currentRecords.map((item, index) => ({
                  ...item,
                  id: indexOfFirstRecord + index + 1,
                  originalId: item.id,
                  areaName: item.area?.name || '', 
                  createdAt: new Date(item.created_At).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  }),
                  acciones: { 
                    editar: () => handleEdit(item),
                    eliminar: () => handleDelete(item.id)
                  }
                }))}
                config={tableConfig}
                showRadioButtons={false}
                showPagination={true}
                currentPage={currentPage}
                totalPages={Math.ceil(filteredPurchasingGroups.length / recordsPerPage)}
                changePage={changePage}
                recordsPerPage={recordsPerPage}
                totalRecords={filteredPurchasingGroups.length}
                collapsed={false}
              />
            </div>
          )}
        </div>
      </div>
      
      <ModalDelete 
        showModalDelete={showModalDelete}
        closeModal={() => setShowModalDelete(false)}
        accept={handleConfirmDelete}
        textType="grupo de compra"
      />

      <ModalSuccess 
        showModalSuccess={showModalSuccess}
        closeModalSuccess={closeModalSuccess}
        fill="green"
        text={modalSuccessText}
      />

      <ModalAlertWarning 
        showModalAlertWarning={showModalAlertWarning}
        closeModal={() => setShowModalAlertWarning(false)}
        error={errorMessage}
        accept={() => setShowModalAlertWarning(false)}
      />
    </div>
  );
}